/**
 * Store Initializer Component
 *
 * Handles app-wide initialization logic on mount.
 * This component doesn't render anything, it just runs initialization side effects.
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";

// Routes that don't require authentication
const PUBLIC_ROUTES = ["/login", "/register"];

export function StoreInitializer() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { initialize, isInitialized, isLoading, isHydrated, user } =
    useAuthStore();

  useEffect(() => {
    // Initialize auth on app mount
    initialize();
  }, [initialize]);

  useEffect(() => {
    // CRITICAL: Wait for initialization to complete before ANY redirects
    // This prevents redirect loops on first load
    if (!isInitialized || isLoading) {
      return;
    }

    // Normalize pathname by removing trailing slash
    const normalizedPath =
      pathname.endsWith("/") && pathname !== "/"
        ? pathname.slice(0, -1)
        : pathname;
    const isPublicRoute = PUBLIC_ROUTES.includes(normalizedPath);

    // If user is not authenticated and trying to access a protected route
    if (!user && !isPublicRoute) {
      navigate("/login");
      return;
    }

    // If user is authenticated and on login/register page, redirect to home
    if (user && isPublicRoute) {
      navigate("/");
      return;
    }
  }, [isInitialized, isLoading, user, pathname, navigate]);

  return null; // This component doesn't render anything
}
